import { useState } from "react";
import { useClient } from "./settings";
import { useDispatch } from "react-redux";
import { open } from "../../../redux/sidebarOpenClose/action";
import { PhoneOff, Mic, MicOff, Video, VideoOff, Share, XCircle } from "react-feather";
import VideoCallRecording from "../../ProjectComponents/Agora/VideoCallRecording";
import { UncontrolledTooltip } from "reactstrap";

export default function Controls(props) {
  const [StopRecording, setStopRecording] = useState(false);
  const client = useClient();
  const {
    tracks,
    setStart,
    setInCall,
    setCall,
    setOpen,
    onScreenChoose,
    videoTrackScreenShare,
    setIsCameraEnabled,
    users,
  } = props;

  const [trackState, setTrackState] = useState({ video: true, audio: true });
  const dispatch = useDispatch();

  // ── Audio: only needs enable/disable, never publish/unpublish ─────────────
  const muteAudio = async () => {
    try {
      await tracks[0].setEnabled(!trackState.audio);
      localStorage.setItem("videoCallMic", String(!trackState.audio));
      setTrackState(ps => ({ ...ps, audio: !ps.audio }));
    } catch (err) {
      console.error("Audio toggle failed:", err);
    }
  };

  // ── Video: must publish when turning ON (track may be unpublished after
  //    screen share ended while camera was off) ──────────────────────────────
  const muteVideo = async () => {
    const cameraTrack = tracks[1];
    if (!cameraTrack) return;

    const turningOn = !trackState.video;

    try {
      if (turningOn) {
        // 1. Enable the media track
        await cameraTrack.setEnabled(true);

        // 2. Publish — required if track was unpublished during screen share.
        //    "already published" is not a real error, so we swallow it.
        try {
          await client.publish([cameraTrack]);
        } catch (publishErr) {
          const msg = publishErr?.message?.toLowerCase() ?? "";
          if (!msg.includes("already")) throw publishErr;
          // else: already published, safe to continue
        }

        setTrackState(ps => ({ ...ps, video: true }));
        setIsCameraEnabled?.(true);

      } else {
        // Turning OFF: just disable — keep published so remote sees "camera off"
        await cameraTrack.setEnabled(false);
        setTrackState(ps => ({ ...ps, video: false }));
        setIsCameraEnabled?.(false);
      }
    } catch (err) {
      console.error("Camera toggle failed:", err);
    }
  };

  // ── Leave call ─────────────────────────────────────────────────────────────
  const leaveChannel = async () => {
    try {
      setStopRecording(true);
      await client.leave();
      props.endCall();
      client.removeAllListeners();
      tracks[0]?.close();
      tracks[1]?.close();
      setStart(false);
      setInCall(false);
      setCall(false);
      setOpen(false);
    } catch (err) {
      console.error("Leave channel error:", err);
    }
  };

  const btnBase = {
    width: "50px",
    height: "40px",
    alignItems: "center",
    display: "flex",
    borderRadius: "12px",
    padding: 0,
    justifyContent: "center",
  };
  const iconStyle = { width: "18px", height: "18px" };

  return (
    <div className="d-flex gap-2 gap-sm-3">

      {/* Microphone */}
      <button
        className={`btn btnStyle ${trackState.audio ? "btn-light" : "btn-primary"}`}
        style={btnBase}
        onClick={muteAudio}
        id="TooltipTopMic"
      >
        {trackState.audio ? <Mic style={iconStyle} /> : <MicOff style={iconStyle} />}
      </button>
      <UncontrolledTooltip placement="top" target="TooltipTopMic">
        {trackState.audio ? "Turn off microphone" : "Turn on microphone"}
      </UncontrolledTooltip>

      {/* Camera */}
      <button
        className={`btn btnStyle ${trackState.video ? "btn-light" : "btn-primary"}`}
        style={btnBase}
        onClick={muteVideo}
        id="TooltipTopCam"
      >
        {trackState.video ? <Video style={iconStyle} /> : <VideoOff style={iconStyle} />}
      </button>
      <UncontrolledTooltip placement="top" target="TooltipTopCam">
        {trackState.video ? "Turn off camera" : "Turn on camera"}
      </UncontrolledTooltip>

      {/* Screen Share */}
      <button
        className={`btn btnStyle ${videoTrackScreenShare !== null ? "btn-primary" : "btn-light"}`}
        style={btnBase}
        id="screenShareBtn"
        onClick={() => onScreenChoose(videoTrackScreenShare === null ? "start" : "stop")}
      >
        {videoTrackScreenShare === null
          ? <Share style={iconStyle} />
          : <XCircle style={iconStyle} />
        }
      </button>
      <UncontrolledTooltip placement="top" target="screenShareBtn">
        {videoTrackScreenShare === null ? "Start screen share" : "Stop screen share"}
      </UncontrolledTooltip>

      {/* Leave */}
      <button
        className="btn btn-danger btnStyle"
        style={btnBase}
        onClick={() => {
          if (window.innerWidth >= 991) {
            document.querySelector(".page-main-header")?.classList.remove("open");
            document.querySelector(".page-sidebar")?.classList.remove("open");
            dispatch(open());
          }
          leaveChannel();
        }}
        id="TooltipTopLeave"
      >
        <PhoneOff style={iconStyle} />
      </button>
      <UncontrolledTooltip placement="top" target="TooltipTopLeave">
        Leave Call
      </UncontrolledTooltip>

      {/* Recording */}
      <div className="text-center">
        <VideoCallRecording StopRecording={StopRecording} users={users} client={client} />
      </div>

    </div>
  );
}